import styled from '@emotion/styled';

import theme from 'theme';
import {SizeTokenValue} from 'common';

import {BadgeVariant} from './types';

import type {BadgeProps, BadgeStyles} from './types';
import type {CSSObject} from '@emotion/styled';

const sizeProps: Partial<Record<SizeTokenValue, CSSObject>> = {
  [SizeTokenValue.Large]: {
    minWidth: 20,
    minHeight: 20,
    borderRadius: 11,
    padding: '0 6px',
  },
  [SizeTokenValue.Medium]: {
    minWidth: 16,
    minHeight: 16,
    borderRadius: 17,
    padding: '0 4px',
  },
  [SizeTokenValue.Small]: {
    minWidth: 12,
    minHeight: 12,
    borderRadius: 13,
    padding: '0 3px',
    fontSize: '10px',
  },
};

const variantColor: Record<BadgeVariant, CSSObject> = {
  [BadgeVariant.Red]: {
    color: theme.colors.white,
    backgroundColor: theme.colors.additional.red[80],
  },

  [BadgeVariant.Accent]: {
    color: theme.colors.white,
    backgroundColor: theme.colors.accent[90],
  },

  [BadgeVariant.Grey]: {
    color: theme.colors.grey[100],
    backgroundColor: theme.colors.grey[30],
  },

  [BadgeVariant.Disabled]: {
    color: theme.colors.grey[40],
    backgroundColor: theme.colors.grey[20],
  },
};

const Container = styled.div<BadgeStyles>`
  display: flex;
  position: relative;
  box-sizing: border-box;
  text-align: center;
  user-select: none;
  align-items: center;
  justify-content: center;
  line-height: 0 !important;
  outline-style: solid;
  outline-width: 2px;

  ${({variant, size, hasStroke, ...props}) => {
    const variantProps = variantColor[variant];

    const color = props.color ?? variantProps.color;
    const backgroundColor = props.backgroundColor ?? variantProps.backgroundColor;
    const borderColor = props.borderColor ?? variantProps.borderColor;

    return {
      ...sizeProps[size],
      ...theme.typography.p3,

      color,
      backgroundColor,
      borderColor,

      outlineColor: hasStroke ? borderColor : 'transparent',
    };
  }}
`;

export const Badge: React.FC<BadgeProps> = props => {
  const {
    qa = 'badge',
    variant = BadgeVariant.Red,
    size = SizeTokenValue.Medium,
    borderColor = theme.colors.grey[10],

    ...rest
  } = props;

  const containerProps = {size, borderColor, variant, ...rest};

  return <Container data-qa={qa} {...containerProps} />;
};
